import { FlowStep } from '../models/enums';

/**
 * Minimal slice of the session project that the results-protection lock
 * needs. Mirrors the shape the dirty-marker reads in `SessionService`.
 * `status` 4 = Done (no frontend ProjectStatus enum exists; the codebase
 * compares against the literal, e.g. results.component `resultsVisible`).
 */
export interface ResultsLockProjectSlice {
  status?: number;
  runSummary?: { done?: number; total?: number } | null;
}

/**
 * True when the project has results from a previous run worth protecting.
 *
 * Superset of the auto-save dirty-marker predicate in `SessionService`:
 * the dirty badge only exists for multi-MF masters (`runSummary` with
 * dispatched/finished children), while the lock must also protect
 * single-case projects, whose only "has results" signal is status Done (4)
 * — the same check `results.component#resultsVisible` uses.
 */
export function projectHasRunResults(
  project: ResultsLockProjectSlice | null | undefined,
): boolean {
  if (!project) return false;
  if (project.status === 4) return true;
  const runSummary = project.runSummary;
  return (
    !!runSummary &&
    ((runSummary.done ?? 0) >= 1 || (runSummary.total ?? 0) >= 1)
  );
}

/**
 * Flow steps whose content feeds the optimizer and is therefore gated
 * behind the results-protection confirm. RESULTS is read-only by nature.
 */
const LOCKABLE_FLOW_STEPS: ReadonlySet<FlowStep> = new Set([
  FlowStep.MATERIALS,
  FlowStep.GEOMETRY,
  FlowStep.LOADCASES,
  FlowStep.GOALS,
  FlowStep.MANUFACTURING,
]);

export function isLockableFlowStep(step: FlowStep): boolean {
  return LOCKABLE_FLOW_STEPS.has(step);
}
